/**
 * Debounces the "Connecting…" / "Not connected" banner's visibility.
 *
 * A fresh chat's socket doesn't exist until the first message is sent, so it
 * cycles ``disconnected -> connecting -> connected`` right as the chat mounts.
 * On a fast connection that handshake resolves in well under a second, so
 * surfacing the banner immediately (per ``useGracefulConnectionStatus``'s
 * deliberate cold-start passthrough) reads as a flicker rather than useful
 * feedback. This hook holds the banner hidden for ``delayMs`` — long enough
 * that a normal handshake never shows it — and only reveals it if the
 * non-connected status actually persists, so a genuinely slow connection
 * still gets the warning.
 *
 * Purely a display concern: it does NOT gate the composer or any other
 * status-driven logic — those continue to read the real status directly so
 * disabled/loading behavior stays correct even while the banner is held back.
 */

import { useEffect, useRef, useState } from 'react';

import type { EpiSocketStatus } from './useEpiStudyAgentSocket';

export const CONNECTION_ALERT_DELAY_MS = 300;

export const useConnectionAlertVisible = (
  status: EpiSocketStatus,
  delayMs: number = CONNECTION_ALERT_DELAY_MS
): boolean => {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (status === 'connected') {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setVisible(false);
      return;
    }
    // Already counting down (or already shown) for this outage — substatus
    // flaps (disconnected <-> connecting) before the delay elapses must not
    // restart the timer.
    if (visible || timerRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setVisible(true);
    }, delayMs);
  }, [status, delayMs, visible]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  return visible;
};
